import { collection, doc, setDoc, query, where, getDocs, Firestore } from 'firebase/firestore';
import { ShopifyService } from './ShopifyService';
import { Product } from '../../types/product';

const shopifyService = ShopifyService.getInstance();

interface ImportOptions {
  mode: 'url' | 'search' | 'bulk';
  urls?: string[];
  storeName: string;
  query?: string;
  maxProducts?: number;
}

export class ShopifyImportService {
  private static instance: ShopifyImportService;
  private existingProductsCache: Map<string, boolean> = new Map();
  private lastCacheUpdate: number = 0;
  private readonly CACHE_TTL = 60000; // 1 minute cache TTL

  private constructor() {}

  static getInstance(): ShopifyImportService {
    if (!ShopifyImportService.instance) {
      ShopifyImportService.instance = new ShopifyImportService();
    }
    return ShopifyImportService.instance;
  }

  private getDb(): Firestore {
    // Import directly from the already initialized firebase config
    const { db } = require('../../config/firebase');
    return db;
  }

  private async clearProductCache() {
    this.existingProductsCache.clear();
    this.lastCacheUpdate = 0;
  }

  private extractBulletPoints(html: string): string[] {
    if (!html) return [];

    // Extract bullet points from <li> tags
    const bulletPoints: string[] = [];
    const liRegex = /<li[^>]*>(.*?)<\/li>/g;
    let match;

    while ((match = liRegex.exec(html)) !== null) {
      const text = match[1]
        .replace(/<[^>]+>/g, '') // Remove any nested HTML tags
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim();

      if (text) {
        bulletPoints.push(text);
      }
    }

    return bulletPoints;
  }

  private cleanDescription(html: string): string {
    if (!html) return '';

    // Remove all HTML tags and decode entities
    return html
      .replace(/<[^>]+>/g, ' ') // Replace tags with space to prevent word joining
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
  }

  private async transformShopifyProduct(product: any, storeName: string): Promise<Product> {
    const description = product.body_html || '';
    const bulletPoints = this.extractBulletPoints(description);
    const cleanDescription = this.cleanDescription(description);

    // Get tags
    const tags = Array.isArray(product.tags) 
      ? product.tags.map(String)
      : (product.tags || '').split(',').map((t: string) => t.trim());

    // Get SKU
    const sku = product.variants?.[0]?.sku || '';

    // Process images - use Shopify CDN URLs directly
    const images = (product.images || []).map((img: any, index: number) => ({
      url: img.src,
      thumbnailUrl: img.src.replace(/\.([^.]+)$/, '_120x120.$1'),
      position: index + 1
    }));


    // Create product ID
    const productId = `SHOPIFY-${product.id}`;

    // Create Shopify link with public domain
    const storeDomainMap: { [key: string]: string } = {
      'Avant Grub': 'avantgrub.com',
      'Witty Yeti': 'wittyyeti.com'
    };
    const publicDomain = storeDomainMap[product.vendor] || `${product.vendor.toLowerCase()}.myshopify.com`;
    const shopifyLink = `https://${publicDomain}/products/${product.handle}`;

    // Create timestamps
    const now = new Date();

    return {
      id: productId,
      source: 'Shopify',
      basicInfo: {
        sku,
        title: product.title?.trim() || '',
        description: cleanDescription,
        metaDescription: product.meta_description || product.metafields?.find((m: any) => m.namespace === 'global' && m.key === 'description_tag')?.value || '',
        pageTitle: product.metafields?.find((m: any) => m.namespace === 'global' && m.key === 'title_tag')?.value || '',
        bulletPoints,
        categories: tags
      },
      links: {
        shopify: shopifyLink
      },
      images,
      optimizations: {},
      status: 'Active',
      storeName,
      timestamps: {
        created: now,
        updated: now,
        imported: now
      }
    };
  }

  private async batchCheckProductsExist(productIds: string[]): Promise<Set<string>> {
    const now = Date.now();
    const existingIds = new Set<string>();
    const uncachedIds = new Set<string>();

    // Check cache first
    if (now - this.lastCacheUpdate < this.CACHE_TTL) {
      productIds.forEach(id => {
        if (this.existingProductsCache.has(id)) {
          if (this.existingProductsCache.get(id)) {
            existingIds.add(id);
          }
        } else {
          uncachedIds.add(id);
        }
      });

      if (uncachedIds.size === 0) {
        return existingIds;
      }
    } else {
      // Cache expired, clear it
      await this.clearProductCache();
      productIds.forEach(id => uncachedIds.add(id));
    }

    const db = this.getDb();
    const productsRef = collection(db, 'products');
    const chunks = [];
    
    // Split uncached IDs into chunks of 10
    const uncachedArray = Array.from(uncachedIds);
    for (let i = 0; i < uncachedArray.length; i += 10) {
      chunks.push(uncachedArray.slice(i, i + 10));
    }

    // Query each chunk
    await Promise.all(chunks.map(async (chunk) => {
      const q = query(productsRef, where('id', 'in', chunk));
      const snapshot = await getDocs(q);
      
      // Update cache and result set
      chunk.forEach(id => {
        const exists = snapshot.docs.some(doc => doc.data().id === id);
        this.existingProductsCache.set(id, exists);
        if (exists) {
          existingIds.add(id);
        }
      });
    }));

    this.lastCacheUpdate = now;
    return existingIds;
  }

  async importProducts(options: ImportOptions, onProgress?: (progress: number) => void): Promise<Product[]> {
    const importedProducts: Product[] = [];
    const store = shopifyService.getStoreConfig(options.storeName);
    const db = this.getDb();

    try {
      switch (options.mode) {
        case 'url': {
          if (!options.urls?.length) {
            throw new Error('No URLs provided for import');
          }

          // First, get all products
          const productsToProcess = await Promise.all(
            options.urls.map(async url => {
              const handle = url.split('/products/')[1]?.split('?')[0];
              if (!handle) {
                throw new Error(`Invalid product URL: ${url}`);
              }
              const response = await shopifyService.getProductByHandle(store, handle);
              return response.product;
            })
          );

          // Batch check existence
          const productIds = productsToProcess.map(p => `SHOPIFY-${p.id}`);
          const existingIds = await this.batchCheckProductsExist(productIds);

          // Process products that don't exist
          for (let i = 0; i < productsToProcess.length; i++) {
            const product = productsToProcess[i];
            const productId = `SHOPIFY-${product.id}`;

            if (existingIds.has(productId)) {
              
              continue;
            }

            const transformedProduct = await this.transformShopifyProduct(product, options.storeName);
            await setDoc(doc(db, 'products', productId), transformedProduct);
            importedProducts.push(transformedProduct);

            if (onProgress) {
              onProgress((i + 1) / productsToProcess.length * 100);
            }
          }
          break;
        }

        case 'search': {
          if (!options.query) {
            throw new Error('Search query is required');
          }

          const searchResults = await shopifyService.searchProducts(store, options.query);
          const products = searchResults.products || [];

          // Batch check existence
          const productIds = products.map(p => `SHOPIFY-${p.id}`);
          const existingIds = await this.batchCheckProductsExist(productIds);

          // Process products that don't exist
          for (let i = 0; i < products.length; i++) {
            const product = products[i];
            const productId = `SHOPIFY-${product.id}`;

            if (existingIds.has(productId)) {
              
              continue;
            }

            const transformedProduct = await this.transformShopifyProduct(product, options.storeName);
            await setDoc(doc(db, 'products', productId), transformedProduct);
            importedProducts.push(transformedProduct);

            if (onProgress) {
              onProgress((i + 1) / products.length * 100);
            }
          }
          break;
        }

        case 'bulk': {
          let totalImported = 0;
          let nextPageCursor: string | null = null;
          let hasMore = true;
          const pageSize = 250; // Use maximum page size for efficiency
          
          // Get total product count first
          const totalProducts = await shopifyService.getTotalProductCount(store);
          let processedProducts = 0;

          

          while (hasMore) {
            // Calculate estimated time remaining
            const avgTimePerProduct = 1000; // 1 second per product on average
            const remainingProducts = totalProducts - processedProducts;
            const estimatedTimeRemaining = Math.ceil(remainingProducts * avgTimePerProduct / 1000); // in seconds
            
            const params: Record<string, unknown> = {
              limit: pageSize,
              fields: 'id,title,body_html,handle,tags,variants,images,vendor'
            };
            if (nextPageCursor) {
              params.page_info = nextPageCursor;
            }

            const bulkResults = await shopifyService.bulkImportProducts(store, pageSize);
            const products = bulkResults.products || [];
            hasMore = bulkResults.hasNextPage;
            nextPageCursor = bulkResults.nextPageCursor || null;

            if (products.length === 0) break;

            // Batch check existence for this page
            const productIds = products.map(p => `SHOPIFY-${p.id}`);
            const existingIds = await this.batchCheckProductsExist(productIds);

            // Process products that don't exist
            for (const product of products) {
              const productId = `SHOPIFY-${product.id}`;
              processedProducts++;

              if (existingIds.has(productId)) {
                
                continue;
              }

              const transformedProduct = await this.transformShopifyProduct(product, options.storeName);
              await setDoc(doc(db, 'products', productId), transformedProduct);
              importedProducts.push(transformedProduct);
              totalImported++;

              if (onProgress) {
                // Calculate progress based on actual total and add artificial delay
                const baseProgress = (processedProducts / totalProducts) * 100;
                // Slow down progress updates by spreading them out
                const adjustedProgress = Math.min(baseProgress * 0.9, hasMore ? 95 : 100);
                onProgress(adjustedProgress);
                
                // Add a small delay between each product to slow down progress
                await new Promise(resolve => setTimeout(resolve, 250));
              }
            }

            // Add a small delay between pages to respect rate limits
            if (hasMore) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }

          // Ensure we show 100% at the end
          if (onProgress) {
            onProgress(100);
          }

          
          break;
        }

        default:
          throw new Error(`Invalid import mode: ${options.mode}`);
      }

      return importedProducts;

    } catch (error) {
      throw error;
    }
  }
}

export const shopifyImportService = ShopifyImportService.getInstance();